import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Avatar, Chip, Button, Skeleton,
  useTheme, alpha, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, Tooltip,
  LinearProgress, Divider, IconButton, CircularProgress,
} from '@mui/material';
import {
  ArrowBack, Timeline, Assessment, People, Stadium,
  ArrowUpward, ArrowDownward, AttachMoney, TrendingUp, SportsBaseball,
  Star, StarBorder,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, PieChart, Pie, Cell, Legend,
} from 'recharts';

import { teamsApi, playersApi, analyticsApi, statsApi } from '../services/apiService';
import { themeUtils } from '../theme/theme';
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';
import UpcomingSchedule from '../components/UpcomingSchedule';
import { useYear } from '../context/YearContext';
import { useFavorites } from '../context/FavoritesContext';
import { getCVRDisplay } from '../utils/cvrCalculations';
import { percentileColor } from '../components/player/PercentileRails';
import useMobile from '../hooks/useMobile';

// ════════════════════════════════════════════════════════════════════════════
// Utilities
// ════════════════════════════════════════════════════════════════════════════
const getTeamLogoUrl = (tc) => getSharedTeamLogoUrl(tc, 500);
const getNestedValue = (o, p) => p.split('.').reduce((c, k) => c && c[k], o);
const fmtSalary = (v) => {
  if (!v) return '\u2014';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return '$' + (v / 1e3).toFixed(0) + 'K';
  return '$' + v.toLocaleString();
};
const fmtAvg = (v) => (v != null && !isNaN(v)) ? parseFloat(v).toFixed(3) : '\u2014';
const fmtDec = (v, d = 2) => (v != null && !isNaN(v)) ? parseFloat(v).toFixed(d) : '\u2014';
const fmtPct = (v) => (v != null && !isNaN(v)) ? (parseFloat(v) * 100).toFixed(1) + '%' : '\u2014';
const fmtInt = (v) => (v != null && !isNaN(v)) ? Math.round(v).toString() : '\u2014';
const makeSlug = (name) => {
  return (name || '').toLowerCase().replace(/\s+/g, '-').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9-]/g, '');
};

// Savant-style heat-map tints (same bands as Players.js) keyed by player name
const buildHeatMap = (rows, key, invert = false) => {
  const vals = rows.map(r => {
    const v = getNestedValue(r, key);
    return (v == null) ? null : parseFloat(v);
  }).filter(v => v != null && !isNaN(v));
  if (vals.length < 2) return {};
  const sorted = [...vals].sort((a, b) => a - b);
  const pctOf = (v) => { const idx = sorted.findIndex(x => x >= v); return idx < 0 ? 1 : idx / (sorted.length - 1); };
  const out = {};
  for (const r of rows) {
    const raw = getNestedValue(r, key);
    const v = (raw == null) ? NaN : parseFloat(raw);
    if (isNaN(v)) { out[r.name] = null; continue; }
    const pc = invert ? 1 - pctOf(v) : pctOf(v);
    let bg, fg;
    if      (pc >= 0.80) { bg = 'rgba(0,200,83,0.17)';   fg = '#00c853'; }
    else if (pc >= 0.60) { bg = 'rgba(124,179,66,0.12)'; fg = '#7cb342'; }
    else if (pc >= 0.40) { bg = 'transparent';           fg = 'inherit'; }
    else if (pc >= 0.20) { bg = 'rgba(239,108,0,0.11)';  fg = '#ef6c00'; }
    else                 { bg = 'rgba(198,40,40,0.14)';  fg = '#c62828'; }
    out[r.name] = { bg, fg };
  }
  return out;
};

const INVERT_STATS = new Set([
  'stats.pitching.era', 'stats.pitching.whip', 'stats.pitching.fip',
  'stats.pitching.baseOnBalls', 'stats.pitching.losses',
  'stats.batting.strikeOuts', 'salary.salary',
]);

// Team CVR display uses shared getCVRDisplay from cvrCalculations.js
// Fed with avgACVR (average player universalCVR, 0-100 scale)
const getWARDisplayTeam = (war) => {
  const v = war || 0;
  if (v >= 30) return { value: v.toFixed(2), color: '#ff6b35', grade: 'A+', desc: 'Championship' };
  if (v >= 20) return { value: v.toFixed(2), color: '#ff8c42', grade: 'A',  desc: 'Contender' };
  if (v >= 10) return { value: v.toFixed(2), color: '#ffd23f', grade: 'B+', desc: 'Above Avg' };
  if (v >= 5)  return { value: v.toFixed(2), color: '#06ffa5', grade: 'B',  desc: 'Average' };
  if (v >= 0)  return { value: v.toFixed(2), color: '#4fb3d9', grade: 'C',  desc: 'Below Avg' };
  return              { value: v.toFixed(2), color: '#c0392b', grade: 'D',  desc: 'Poor' };
};

// Reusable Stat Badge matching PlayerDetail header badges
const StatBadge = ({ label, value, sub, color, tooltip }) => {
  const inner = (
    <Box sx={{
      textAlign: 'center', px: 1.25, py: 0.75, borderRadius: 2,
      border: `1px solid ${alpha(color, 0.3)}`,
      background: alpha(color, 0.06),
      cursor: tooltip ? 'help' : 'default', minWidth: 52,
    }}>
      <Typography variant="caption" sx={{
        fontSize: { xs: '0.75rem', sm: '0.75rem' }, fontWeight: 700, color,
        textTransform: 'uppercase', letterSpacing: 0.5, display: 'block',
      }}>{label}</Typography>
      <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', lineHeight: 1, color }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, color: 'text.secondary', display: 'block' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip} arrow placement="bottom" enterDelay={200}>{inner}</Tooltip> : inner;
};

// ════════════════════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════════════════════
const TeamDetail = () => {
  const { teamId, year: yearParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentYear } = useYear();
  const { toggleFavoriteTeam, isTeamFavorite } = useFavorites();
  const year = yearParam || currentYear;

  // Seed instantly from the teams-list row data if navigated from /teams.
  // The full API response overwrites this once loaded in the background.
  const prefill = location.state?.prefill || null;

  const [loading, setLoading] = useState(!prefill);
  const [error, setError] = useState(null);
  const [team, setTeam] = useState(prefill || null);
  const [roster, setRoster] = useState([]);
  const [gameLog, setGameLog] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [projections, setProjections] = useState(null);
  const [movers, setMovers] = useState(null);
  const [cycleIndex, setCycleIndex] = useState(null);
  const { isMobile } = useMobile();

  const loadTeamData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [teamRes, rosterRes, schedRes] = await Promise.all([
        teamsApi.getTeam(teamId, { year, mode: 'rolling' }),
        // category:'all' — default is 'batting', which silently drops every pitcher
        playersApi.getPlayers({ team: teamId, year, limit: 200, category: 'all', mode: 'rolling' }),
        teamsApi.getTeamSchedule(teamId, { year, limit: 200 }).catch(() => ({ games: [] })),
      ]);
      setTeam(teamRes);
      setRoster(rosterRes.players || []);
      // Schedule API returns most-recent-first — normalize to chronological
      // ascending so .slice(-N) semantics ("last N games") work everywhere.
      setGameLog((schedRes.games || []).slice().sort((a, b) => (a.date || '').localeCompare(b.date || '')));
      // Load projections in background (non-blocking)
      teamsApi.getTeamProjections(teamId, { days: 30 }).then(p => setProjections(p)).catch(() => {});
      // Load CVR movers + Cycle Index in background (non-blocking)
      statsApi.getCVRMovers({ year, team: teamId, baseline: 'snapshot', days: 7, minGames: 3, minDelta: 2, limit: 5 })
        .then(m => setMovers(m)).catch(() => {});
      statsApi.getCycleIndex({ year }).then(ci => setCycleIndex(ci)).catch(() => {});
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [teamId, year]);

  useEffect(() => { loadTeamData(); }, [loadTeamData]);

  // Build win probability map keyed by gamePk for UpcomingSchedule
  const winProbabilities = useMemo(() => {
    if (!projections?.games) return null;
    const map = {};
    projections.games.forEach(g => {
      map[g.gamePk] = { winProbability: g.winProbability, factors: g.factors };
    });
    return map;
  }, [projections]);

  const record = useMemo(() => {
    if (!gameLog || !gameLog.length) return { wins: (team && team.record && team.record.wins) || 0, losses: (team && team.record && team.record.losses) || 0 };
    const w = gameLog.filter(g => g.result === 'W').length;
    const l = gameLog.filter(g => g.result === 'L').length;
    return { wins: w, losses: l };
  }, [gameLog, team]);

  if (loading) return <TeamDetailSkeleton />;
  if (error) return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography color="error" variant="h6">Something went wrong. Please try again.</Typography>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
    </Box>
  );

  const teamName = (team && team.name) || teamId;
  const teamColor = themeUtils.getTeamColor(teamId) || '#1976d2';
  const logoUrl = getTeamLogoUrl(teamId);
  // seasonStats.batting/pitching are the canonical nested objects from the API.
  // Fall back to the team root (flat-field schema) so older / partial responses
  // still render values instead of em-dashes.
  const batting = (team && team.seasonStats && team.seasonStats.batting && Object.keys(team.seasonStats.batting).length > 0)
    ? team.seasonStats.batting
    : (team || {});
  const pitching = (team && team.seasonStats && team.seasonStats.pitching && Object.keys(team.seasonStats.pitching).length > 0)
    ? team.seasonStats.pitching
    : (team || {});
  const standings = (team && team.standings) || {};
  const analytics = (team && team.analytics) || {};
  const contract = (team && team.contractSummary) || {};
  // Prefer the root-level `war` object — `analytics.overall.war` was historically
  // overwritten with a number, which made `war.total` undefined.
  const teamWarObj = (team && team.war && typeof team.war === 'object') ? team.war : null;
  const overallWarObj = (analytics && analytics.overall && analytics.overall.war && typeof analytics.overall.war === 'object') ? analytics.overall.war : null;
  const overallWarNum = (analytics && analytics.overall && typeof analytics.overall.war === 'number') ? analytics.overall.war : null;
  const war = teamWarObj || overallWarObj || (overallWarNum != null ? { total: overallWarNum, batting: 0, pitching: 0 } : {});
  const totalPayroll = contract.totalPayroll || (team && team.totalPayroll) || 0;
  const teamCVR = (team && team.cvr) || (team && team.avgACVR) || 0;
  const teamACVR = (team && team.acvr) || (team && team.avgACVR) || 0;
  const cvrD = getCVRDisplay(teamCVR);  // Raw performance grade (0-100)
  const acvrD = getCVRDisplay(teamACVR);  // Bang-for-buck grade (0-100)
  const warD = getWARDisplayTeam(war.total || 0);
  const gp = record.wins + record.losses;
  const winPct = gp > 0 ? (record.wins / gp) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: 'auto', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
        {/* Back Button */}
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}
          size="small" sx={{ mb: 1, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
          Back
        </Button>

        {/* HEADER - mirrors PlayerDetail style */}
        <Card elevation={0} sx={{
          border: `1px solid ${alpha(teamColor, 0.15)}`, borderRadius: 2, overflow: 'hidden', mb: 2,
        }}>
          <Box sx={{ height: 3, background: teamColor }} />
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
              <Avatar src={logoUrl} alt={teamId}
                sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, bgcolor: teamColor, fontSize: '1rem', fontWeight: 700 }}
                imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>
                {teamId}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: { xs: 150, sm: 200 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, lineHeight: 1.1, mb: 0.25 }}>
                    {teamName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); toggleFavoriteTeam(teamId); }}
                    sx={{ ml: 0.5 }}
                  >
                    {isTeamFavorite(teamId) ? (
                      <Star sx={{ color: '#FFD700', fontSize: 22 }} />
                    ) : (
                      <StarBorder sx={{ fontSize: 22, color: 'text.disabled' }} />
                    )}
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                  <Chip label={record.wins + '-' + record.losses} size="small"
                    sx={{ bgcolor: teamColor, color: '#fff', fontWeight: 700, fontSize: '0.75rem', height: 24 }} />
                  <Chip label={(team && team.division) || ''} size="small" variant="outlined"
                    sx={{ borderColor: alpha(teamColor, 0.3), fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', mb: 0.5 }}>
                  {Array.from({ length: currentYear - 2015 + 1 }, (_, i) => 2015 + i).map((y) => {
                    const isActive = String(y) === String(year);
                    return (
                      <Chip
                        key={y}
                        label={String(y).slice(-2)}
                        size="small"
                        variant={isActive ? 'filled' : 'outlined'}
                        onClick={() => navigate(`/teams/${teamId}/${y}`)}
                        sx={{
                          fontWeight: isActive ? 800 : 600,
                          fontSize: '0.7rem',
                          height: 20,
                          minWidth: 28,
                          cursor: 'pointer',
                          '& .MuiChip-label': { px: 0.6 },
                          ...(isActive ? {
                            backgroundColor: teamColor,
                            color: '#fff',
                            '&:hover': { backgroundColor: alpha(teamColor, 0.85) },
                          } : {
                            borderColor: alpha(teamColor, 0.25),
                            color: 'text.secondary',
                            '&:hover': { backgroundColor: alpha(teamColor, 0.08), borderColor: alpha(teamColor, 0.5) },
                          }),
                        }}
                      />
                    );
                  })}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {winPct.toFixed(3)} W% &middot; {standings.runDifferential >= 0 ? '+' : ''}{standings.runDifferential || 0} Run Diff
                  {totalPayroll > 0 ? ' \u00B7 ' + fmtSalary(totalPayroll) + ' Payroll' : ''}
                </Typography>
              </Box>

              {/* Quick Stat Badges */}
              <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <StatBadge label="CVR" value={cvrD.value} sub={`${cvrD.grade} ${cvrD.emoji || ''}`} color={cvrD.color}
                  tooltip={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>Team CVR — Raw Performance</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Playing-time-weighted average of player Composite Value Ratings (0-100). Measures pure on-field production regardless of payroll.</Typography>
                    </Box>
                  } />
                <StatBadge label="ACVR" value={acvrD.value} sub={`${acvrD.grade} ${acvrD.emoji || ''}`} color={acvrD.color}
                  tooltip={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>Team ACVR — Bang for Buck</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>CVR adjusted for payroll efficiency. A cheap team with strong CVR scores higher than an expensive one with equal CVR.</Typography>
                    </Box>
                  } />
                <StatBadge label="Salary" value={fmtSalary(totalPayroll)} sub={(contract.playerCount || 0) + ' players'} color="#4CAF50"
                  tooltip={totalPayroll > 0 ? (
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>Payroll Breakdown</Typography>
                      {contract.payrollStats && (
                        <React.Fragment>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>Median: {fmtSalary(contract.payrollStats.median)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>Top 5: {fmtSalary(contract.payrollStats.top5Total)} ({(contract.payrollStats.top5Pct || 0).toFixed(1)}%)</Typography>
                        </React.Fragment>
                      )}
                    </Box>
                  ) : undefined} />
                <StatBadge label="WAR" value={warD.value} sub={warD.grade} color={warD.color}
                  tooltip={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>Team WAR</Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>Batting: {(war.batting || 0).toFixed(2)}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>Pitching: {(war.pitching || 0).toFixed(2)}</Typography>
                    </Box>
                  } />

              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Upcoming Schedule */}
        <UpcomingSchedule teamId={teamId} teamColor={teamColor} days={14} winProbabilities={winProbabilities} projections={projections} />

        {/* Tabs */}
        <Card elevation={0} sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant={isMobile ? 'fullWidth' : 'scrollable'} scrollButtons={isMobile ? false : 'auto'}
            sx={{
              minHeight: 44, px: isMobile ? 0 : 1,
              '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: isMobile ? '0.7rem' : '0.8rem', py: 0, px: isMobile ? 0.5 : 1.5, gap: 0.5, minWidth: isMobile ? 0 : undefined, '& .MuiSvgIcon-root': { fontSize: '0.9rem' } },
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: teamColor },
              '& .Mui-selected': { color: teamColor + ' !important' },
            }}>
            <Tab icon={isMobile ? undefined : <Stadium />} label="Overview" iconPosition="start" />
            <Tab icon={isMobile ? undefined : <Assessment />} label="Stats" iconPosition="start" />
            <Tab icon={isMobile ? undefined : <People />} label="Roster" iconPosition="start" />
            <Tab icon={isMobile ? undefined : <Timeline />} label="Games" iconPosition="start" />
            <Tab icon={isMobile ? undefined : <AttachMoney />} label="Contracts" iconPosition="start" />
          </Tabs>
        </Card>

        {/* Tab Content */}
        {activeTab === 0 && <OverviewTab team={team} record={record} batting={batting} pitching={pitching} standings={standings} analytics={analytics} contract={contract} teamColor={teamColor} teamId={teamId} roster={roster} projections={projections} gameLog={gameLog} movers={movers} cycleIndex={cycleIndex} />}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TeamStatsTab batting={batting} pitching={pitching} fielding={(team && team.seasonStats && team.seasonStats.fielding) || {}} standings={standings} analytics={analytics} record={record} teamColor={teamColor} team={team} />
            <AnalyticsTab teamId={teamId} year={year} teamColor={teamColor} roster={roster} />
          </Box>
        )}
        {activeTab === 2 && <RosterTab roster={roster} teamId={teamId} year={year} teamColor={teamColor} />}
        {activeTab === 3 && <GameLogTab gameLog={gameLog} teamId={teamId} />}
        {activeTab === 4 && <ContractsTab teamId={teamId} year={year} teamColor={teamColor} />}
      </Box>
    </motion.div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TEAM PERCENTILE RAILS — Savant-style ranks vs all 30 teams
// (mirrors PlayerDetail's PercentileRails visual)
// ════════════════════════════════════════════════════════════════════════════
const TEAM_RAIL_DEFS = {
  overall: [
    { label: 'WAR', path: 'war.total', fmt: (v) => fmtDec(v, 1) },
    { label: 'Run Diff', path: 'standings.runDifferential', fmt: (v) => (v >= 0 ? '+' : '') + fmtInt(v) },
    { label: 'Pyth W%', path: 'standings.pythagoreanWinPct', fmt: (v) => fmtAvg(v) },
  ],
  batting: [
    { label: 'AVG', path: 'stats.batting.avg', fmt: fmtAvg },
    { label: 'OBP', path: 'stats.batting.obp', fmt: fmtAvg },
    { label: 'SLG', path: 'stats.batting.slg', fmt: fmtAvg },
    { label: 'OPS', path: 'stats.batting.ops', fmt: fmtAvg },
    { label: 'HR', path: 'stats.batting.homeRuns', fmt: fmtInt },
    { label: 'Runs', path: 'stats.batting.runs', fmt: fmtInt },
    { label: 'ISO', path: 'stats.batting.iso', fmt: fmtAvg },
    { label: 'BB%', path: 'stats.batting.bbRate', fmt: fmtPct },
    { label: 'K%', path: 'stats.batting.kRate', fmt: fmtPct, lowerBetter: true },
  ],
  pitching: [
    { label: 'ERA', path: 'stats.pitching.era', fmt: (v) => fmtDec(v, 2), lowerBetter: true },
    { label: 'WHIP', path: 'stats.pitching.whip', fmt: (v) => fmtDec(v, 2), lowerBetter: true },
    { label: 'FIP', path: 'stats.pitching.fip', fmt: (v) => fmtDec(v, 2), lowerBetter: true },
    { label: 'K/9', path: 'stats.pitching.strikeoutsPer9Inn', fmt: (v) => fmtDec(v, 1) },
    { label: 'BB/9', path: 'stats.pitching.walksPer9Inn', fmt: (v) => fmtDec(v, 1), lowerBetter: true },
    { label: 'HR/9', path: 'stats.pitching.homeRunsPer9', fmt: (v) => fmtDec(v, 2), lowerBetter: true },
  ],
};

const TeamRail = ({ row }) => {
  const theme = useTheme();
  const color = percentileColor(row.percentile);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.45 }}>
      <Typography sx={{ width: 64, flexShrink: 0, fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
        {row.label}
      </Typography>
      <Box sx={{ flex: 1, position: 'relative', height: 22, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ position: 'absolute', left: 0, right: 0, height: 5, borderRadius: 2.5, bgcolor: alpha(theme.palette.text.primary, 0.08) }} />
        <Box sx={{ position: 'absolute', left: '50%', top: 2, bottom: 2, width: '1px', bgcolor: alpha(theme.palette.text.primary, 0.18) }} />
        <Box sx={{ position: 'absolute', left: 0, width: `${row.percentile}%`, height: 5, borderRadius: 2.5, bgcolor: alpha(color, 0.55), transition: 'width 0.5s ease' }} />
        <Tooltip title={`${row.percentile}th percentile of 30 teams${row.lowerBetter ? ' (lower is better)' : ''}`} arrow>
          <Box sx={{
            position: 'absolute', left: `calc(${row.percentile}% - 11px)`,
            width: 23, height: 23, borderRadius: '50%', bgcolor: color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: `0 1px 4px ${alpha('#000', 0.35)}`,
            transition: 'left 0.5s ease', zIndex: 1, cursor: 'default',
          }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
              {row.percentile}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <Typography sx={{ width: 52, flexShrink: 0, textAlign: 'right', fontSize: '0.78rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
        {row.display}
      </Typography>
    </Box>
  );
};

const TeamPercentileRails = ({ teamId, year }) => {
  const [league, setLeague] = useState(null);

  useEffect(() => {
    let cancelled = false;
    teamsApi.getTeams({ year, mode: 'rolling', limit: 100, category: 'all' })
      .then(r => { if (!cancelled) setLeague(r.teams || []); })
      .catch(() => { if (!cancelled) setLeague([]); });
    return () => { cancelled = true; };
  }, [year]);

  const sections = useMemo(() => {
    if (!league || league.length < 2) return null;
    const me = league.find(t => t.id === teamId);
    if (!me) return null;
    const buildRows = (defs) => defs.map(def => {
      const vals = league.map(t => {
        const v = getNestedValue(t, def.path);
        return (v == null) ? null : parseFloat(v);
      }).filter(v => v != null && !isNaN(v));
      const raw = getNestedValue(me, def.path);
      const v = (raw == null) ? NaN : parseFloat(raw);
      if (isNaN(v) || vals.length < 2) return null;
      const below = vals.filter(x => x < v).length;
      const equal = vals.filter(x => x === v).length;
      let pct = Math.round(((below + (equal - 1) / 2) / (vals.length - 1)) * 100);
      if (def.lowerBetter) pct = 100 - pct;
      pct = Math.max(0, Math.min(100, pct));
      return { label: def.label, percentile: pct, display: def.fmt(v), lowerBetter: !!def.lowerBetter };
    }).filter(Boolean);
    return {
      overall: buildRows(TEAM_RAIL_DEFS.overall),
      batting: buildRows(TEAM_RAIL_DEFS.batting),
      pitching: buildRows(TEAM_RAIL_DEFS.pitching),
    };
  }, [league, teamId]);

  if (league === null) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Skeleton width={220} height={22} sx={{ mb: 1.5 }} />
          {[...Array(6)].map((_, i) => <Skeleton key={i} height={24} sx={{ my: 0.5 }} />)}
        </CardContent>
      </Card>
    );
  }
  if (!sections) return null;

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            League Percentile Rankings
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
            vs {league.length} teams
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Overall</Typography>
            {sections.overall.map(r => <TeamRail key={r.label} row={r} />)}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Hitting</Typography>
            {sections.batting.map(r => <TeamRail key={r.label} row={r} />)}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Pitching</Typography>
            {sections.pitching.map(r => <TeamRail key={r.label} row={r} />)}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ════════════════════════════════════════════════════════════════════════════
const OverviewTab = ({ team, record, batting, pitching, standings, analytics, contract, teamColor, teamId, roster, projections, gameLog, movers, cycleIndex }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const gp = record.wins + record.losses;
  const rs = standings.runsScored || batting.runs || 0;
  const ra = standings.runsAllowed || pitching.runs || 0;
  const runDiff = rs - ra;
  const pythW = gp > 0 ? Math.round(Math.pow(rs || 1, 1.83) / (Math.pow(rs || 1, 1.83) + Math.pow(ra || 1, 1.83)) * gp) : 0;
  const luck = record.wins - pythW;

  // Last 10 completed games (gameLog is chronological — most recent last)
  const last10 = useMemo(() => {
    return (gameLog || []).filter(g => g.result === 'W' || g.result === 'L').slice(-10);
  }, [gameLog]);
  const last10W = last10.filter(g => g.result === 'W').length;

  // This team's row in the league-wide Cycle Index leaderboard
  const ciTeams = cycleIndex?.teams || [];
  const ciEntry = useMemo(() => ciTeams.find(t => t.team === teamId) || null, [ciTeams, teamId]);

  const moverRisers = movers?.risers || [];
  const moverFallers = movers?.fallers || [];
  const hasMovers = moverRisers.length > 0 || moverFallers.length > 0;

  const radarData = [
    { stat: 'AVG', value: (batting.avg || 0) / 0.280 * 100 },
    { stat: 'OPS', value: (batting.ops || 0) / 0.780 * 100 },
    { stat: 'HR', value: Math.min(120, (batting.homeRuns || 0) / 200 * 100) },
    { stat: 'ERA', value: Math.max(0, (5.5 - (pitching.era || 4.5)) / 2.5 * 100) },
    { stat: 'WHIP', value: Math.max(0, (1.6 - (pitching.whip || 1.3)) / 0.6 * 100) },
    { stat: 'K/9', value: ((pitching.strikeoutsPer9Inn || 0) / 10) * 100 },
    { stat: 'FLDG', value: ((team && team.seasonStats && team.seasonStats.fielding && team.seasonStats.fielding.fieldingPercentage) || 0.98) / 1.0 * 100 },
    { stat: 'BB%', value: Math.max(0, (0.12 - (batting.bbRate || 0.08)) / 0.08 * -100 + 100) },
  ];

  const contractPie = useMemo(() => {
    if (!contract || !contract.contractBreakdown) return [];
    return Object.entries(contract.contractBreakdown)
      .filter(function(e) { return e[1].totalSalary > 0; })
      .map(function(e) {
        return { name: e[0].replace(/-/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); }), value: e[1].totalSalary, count: e[1].count };
      })
      .sort(function(a, b) { return b.value - a.value; });
  }, [contract]);

  const PIE_COLORS = ['#1976d2', '#4CAF50', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#F44336', '#795548'];

  const QuickCard = ({ label, main, sub, color: c }) => (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: { xs: '0.75rem', sm: '0.75rem' }, textTransform: 'uppercase', letterSpacing: 0.5, color: c || teamColor }}>{label}</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', lineHeight: 1.2, mt: 0.5 }}>{main}</Typography>
        {sub && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );

  const keyMetrics = [
    { label: 'ERA', value: fmtDec(pitching.era), pct: Math.max(0, (5.5 - (pitching.era || 4.5)) / 3 * 100), color: '#4CAF50' },
    { label: 'WHIP', value: fmtDec(pitching.whip), pct: Math.max(0, (1.8 - (pitching.whip || 1.3)) / 0.8 * 100), color: '#2196F3' },
    { label: 'Team AVG', value: fmtAvg(batting.avg), pct: (batting.avg || 0) / 0.300 * 100, color: '#FF9800' },
    { label: 'Team OBP', value: fmtAvg(batting.obp), pct: (batting.obp || 0) / 0.380 * 100, color: '#E91E63' },
    { label: 'Team SLG', value: fmtAvg(batting.slg), pct: (batting.slg || 0) / 0.500 * 100, color: '#9C27B0' },
    { label: 'K/9', value: fmtDec(pitching.strikeoutsPer9Inn, 1), pct: (pitching.strikeoutsPer9Inn || 0) / 12 * 100, color: '#00BCD4' },
    { label: 'BB/9', value: fmtDec(pitching.walksPer9Inn, 1), pct: Math.max(0, (5 - (pitching.walksPer9Inn || 3.5)) / 3 * 100), color: '#F44336' },
    { label: 'FIP', value: fmtDec(pitching.fip), pct: Math.max(0, (6 - (pitching.fip || 4)) / 3 * 100), color: '#795548' },
  ];

  const topWar = roster.filter(function(p) { return (p.war || 0) > 0; })
    .sort(function(a, b) { return (b.war || 0) - (a.war || 0); }).slice(0, 8);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}><QuickCard label="Record" main={record.wins + '-' + record.losses} sub={(gp > 0 ? (record.wins / gp).toFixed(3) : '0.000') + ' Win%'} /></Grid>
      <Grid item xs={6} sm={3}><QuickCard label="Run Diff" main={(runDiff >= 0 ? '+' : '') + runDiff} sub={(batting.runs || 0) + ' RS / ' + (pitching.runs || 0) + ' RA'} color={runDiff >= 0 ? '#2e7d32' : '#c62828'} /></Grid>
      <Grid item xs={6} sm={3}><QuickCard label="Pythagorean" main={pythW + '-' + (gp - pythW)} sub={(luck >= 0 ? '+' : '') + luck + ' luck wins'} /></Grid>
      <Grid item xs={6} sm={3}><QuickCard label="Team OPS" main={fmtAvg(batting.ops)} sub={fmtAvg(batting.avg) + ' AVG / ' + fmtAvg(batting.obp) + ' OBP'} /></Grid>

      {/* Last 10 form strip */}
      {last10.length > 0 && (
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 1.5, pb: '12px !important', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: teamColor }}>
                Last {last10.length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {last10.map((g, i) => {
                  const isW = g.result === 'W';
                  const score = g.score || {};
                  return (
                    <Tooltip key={i} arrow title={`${g.homeAway === 'home' ? 'vs' : '@'} ${g.opponent || ''} \u00B7 ${g.result} ${score.team ?? ''}-${score.opponent ?? ''}`}>
                      <Box
                        onClick={() => { if (g.gameId && g.date) navigate('/boxscore/' + g.gameId + '/' + g.date); }}
                        sx={{
                          width: 26, height: 26, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: '0.72rem', cursor: g.gameId ? 'pointer' : 'default',
                          bgcolor: isW ? alpha('#4caf50', 0.18) : alpha('#f44336', 0.15),
                          color: isW ? '#2e7d32' : '#c62828',
                          border: `1px solid ${isW ? alpha('#4caf50', 0.4) : alpha('#f44336', 0.35)}`,
                        }}>
                        {g.result}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.85rem', color: last10W >= 5 ? '#2e7d32' : '#c62828' }}>
                {last10W}-{last10.length - last10W}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* League percentile rankings — Savant-style rails vs 30 teams */}
      <Grid item xs={12}>
        <TeamPercentileRails teamId={teamId} year={team && team.year} />
      </Grid>

      {/* Cycle Index + CVR Movers */}
      {(ciEntry || movers) && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {ciEntry && (
              <Grid item xs={12} md={hasMovers ? 6 : 12}>
                <Card elevation={0} sx={{
                  border: `1px solid ${alpha(teamColor, 0.2)}`, borderRadius: 2, height: '100%',
                  background: `linear-gradient(135deg, ${alpha(teamColor, 0.04)}, transparent)`,
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.8rem', color: teamColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        The Cycle Index
                      </Typography>
                      <Chip label={`#${ciEntry.rank} of ${ciTeams.length}`} size="small"
                        sx={{ fontWeight: 800, fontSize: '0.72rem', height: 22, bgcolor: alpha(teamColor, 0.12), color: teamColor }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: '2rem', lineHeight: 1, color: teamColor }}>
                        {typeof ciEntry.cycleIndex === 'number' ? ciEntry.cycleIndex.toFixed(1) : ciEntry.cycleIndex}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        league avg {ciTeams.length ? (ciTeams.reduce((s, t) => s + (t.cycleIndex || 0), 0) / ciTeams.length).toFixed(1) : '\u2014'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 1 }}>
                      Playing-time-weighted roster ACVR — on-field value per dollar spent
                    </Typography>
                    {(ciEntry.top || []).length > 0 && (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 140 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#2e7d32', display: 'block', mb: 0.25 }}>Best Value</Typography>
                          {(ciEntry.top || []).map((p, i) => (
                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 0.5, px: 0.5, '&:hover': { bgcolor: alpha(teamColor, 0.06) } }}
                              onClick={() => navigate('/players/profile/' + makeSlug(p.name))}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{p.name}</Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#2e7d32' }}>{Math.round(p.acvr ?? p.cvr ?? 0)}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 140 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#c62828', display: 'block', mb: 0.25 }}>Worst Value</Typography>
                          {(ciEntry.bottom || []).map((p, i) => (
                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 0.5, px: 0.5, '&:hover': { bgcolor: alpha(teamColor, 0.06) } }}
                              onClick={() => navigate('/players/profile/' + makeSlug(p.name))}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{p.name}</Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#c62828' }}>{Math.round(p.acvr ?? p.cvr ?? 0)}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
            {movers && (
              <Grid item xs={12} md={ciEntry ? 6 : 12}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        CVR Movers — Last 7 Days
                      </Typography>
                    </Box>
                    {!hasMovers && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                        No significant CVR movement on the roster this week.
                      </Typography>
                    )}
                    {[...moverRisers.map(m => ({ ...m, _dir: 'up' })), ...moverFallers.map(m => ({ ...m, _dir: 'down' }))]
                      .slice(0, 8).map((m, i) => (
                        <Box key={m.compositeId || i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', borderRadius: 1, px: 0.5,
                          '&:hover': { bgcolor: alpha(teamColor, 0.05) } }}
                          onClick={() => navigate('/players/profile/' + makeSlug(m.name))}>
                          {m._dir === 'up'
                            ? <ArrowUpward sx={{ fontSize: 15, color: '#2e7d32' }} />
                            : <ArrowDownward sx={{ fontSize: 15, color: '#c62828' }} />}
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontSize: '0.8rem' }}>{m.name}</Typography>
                          <Chip label={m.position || m.playerType || '\u2014'} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {m.baselineCVR}→{m.rollingCVR}
                          </Typography>
                          <Typography variant="body2" fontWeight={800} sx={{ minWidth: 42, textAlign: 'right', fontSize: '0.8rem',
                            color: m._dir === 'up' ? '#2e7d32' : '#c62828' }}>
                            {m.cvrDelta > 0 ? '+' : ''}{m.cvrDelta}
                          </Typography>
                        </Box>
                      ))}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      {/* Projected Record */}
      {projections?.projectedRecord && (
        <Grid item xs={12}>
          <Card elevation={0} sx={{
            border: `1px solid ${alpha(teamColor, 0.15)}`, borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(teamColor, 0.03)}, transparent)`,
          }}>
            <CardContent sx={{ p: 2, pb: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TrendingUp sx={{ fontSize: 20, color: teamColor }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: '0.8rem', color: teamColor, textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>
                      Projected Record
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Based on team performance, run scoring, and remaining schedule
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', lineHeight: 1, color: teamColor }}>
                      {projections.projectedRecord.wins}-{projections.projectedRecord.losses}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {(projections.projectedRecord.wins / (projections.projectedRecord.wins + projections.projectedRecord.losses)).toFixed(3)} W%
                    </Typography>
                  </Box>
                  {projections.confidenceInterval && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem', lineHeight: 1 }}>
                        {projections.confidenceInterval.low}-{projections.confidenceInterval.high}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        95% CI wins
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem', lineHeight: 1 }}>
                      {projections.remainingGames}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Games left
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem', lineHeight: 1 }}>
                      {(projections.avgWinProbability * 100).toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Avg win prob
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Radar Chart */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', mb: 1 }}>Team Profile</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke={alpha(theme.palette.text.primary, 0.1)} />
                <PolarAngleAxis dataKey="stat" tick={{ fontSize: 11, fontWeight: 600 }} />
                <PolarRadiusAxis domain={[0, 120]} tick={false} axisLine={false} />
                <Radar dataKey="value" fill={teamColor} fillOpacity={0.25} stroke={teamColor} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Key Metrics */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', mb: 1.5 }}>Key Metrics</Typography>
            {keyMetrics.map(function(m) {
              return (
                <Box key={m.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                  <Typography variant="caption" sx={{ width: 70, flexShrink: 0, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>{m.label}</Typography>
                  <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: alpha(m.color, 0.08), overflow: 'hidden' }}>
                    <Box sx={{ width: Math.min(100, m.pct) + '%', height: '100%', borderRadius: 4, bgcolor: alpha(m.color, 0.6), transition: 'width 0.6s ease' }} />
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 48, textAlign: 'right', fontSize: '0.8rem' }}>{m.value}</Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Grid>

      {/* Payroll Pie */}
      {contractPie.length > 0 && (
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', mb: 1 }}>Payroll by Contract Type</Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                <PieChart>
                  <Pie data={contractPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={isMobile ? 70 : 100}
                    label={isMobile ? false : function(entry) { return entry.name + ' ' + (entry.percent * 100).toFixed(0) + '%'; }}
                    labelLine={false}>
                    {contractPie.map(function(_, i) { return <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />; })}
                  </Pie>
                  <RTooltip formatter={function(val) { return fmtSalary(val); }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Top Roster WAR */}
      <Grid item xs={12} md={contractPie.length > 0 ? 6 : 12}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', mb: 1.5 }}>Top Roster WAR</Typography>
            {topWar.map(function(p, i) {
              return (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: { xs: 1, sm: 0.4 }, cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(teamColor, 0.04) }, borderRadius: 1, px: 0.5, minHeight: { xs: 44, sm: 'auto' } }}
                  onClick={function() { navigate('/players/profile/' + makeSlug(p.name)); }}>
                  <Typography variant="caption" sx={{ width: 16, fontWeight: 800, color: i < 3 ? teamColor : 'text.secondary', fontSize: '0.75rem' }}>
                    {i + 1}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontSize: '0.8rem' }}>{p.name}</Typography>
                  <Chip label={p.position || 'u2014'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 36, textAlign: 'right', fontSize: '0.8rem', color: (p.war || 0) >= 3 ? '#4CAF50' : 'text.primary' }}>
                    {(p.war || 0).toFixed(2)}
                  </Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TEAM STATS TAB - charts + bar visualizations
// ════════════════════════════════════════════════════════════════════════════
const TeamStatsTab = ({ batting, pitching, fielding, standings, analytics, record, teamColor, team }) => {
  const theme = useTheme();

  const offenseData = [
    { stat: 'AVG', value: batting.avg || 0 },
    { stat: 'OBP', value: batting.obp || 0 },
    { stat: 'SLG', value: batting.slg || 0 },
    { stat: 'ISO', value: batting.iso || 0 },
    { stat: 'wOBA', value: batting.wOBA || 0 },
    { stat: 'BABIP', value: batting.babip || 0 },
  ];

  const pitchingData = [
    { stat: 'ERA', value: pitching.era || 0 },
    { stat: 'FIP', value: pitching.fip || 0 },
    { stat: 'WHIP', value: pitching.whip || 0 },
    { stat: 'K/9', value: pitching.strikeoutsPer9Inn || 0 },
    { stat: 'BB/9', value: pitching.walksPer9Inn || 0 },
    { stat: 'HR/9', value: pitching.homeRunsPer9 || 0 },
  ];

  const StatSection = ({ title, color, stats }) => (
    <Card elevation={0} sx={{ border: '1px solid ' + alpha(color, 0.15), borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Typography>
        </Box>
        {stats.map(function(s) {
          return (
            <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <Typography variant="caption" sx={{ width: 85, flexShrink: 0, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>{s.label}</Typography>
              <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: alpha(color, 0.08), overflow: 'hidden' }}>
                <Box sx={{ width: Math.min(100, s.barPct || 50) + '%', height: '100%', borderRadius: 4, bgcolor: alpha(color, s.invert ? 0.4 : 0.6), transition: 'width 0.5s ease' }} />
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ minWidth: 50, textAlign: 'right', fontSize: '0.8rem' }}>{s.format(s.value)}</Typography>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );

  const winPctVal = (record.wins + record.losses > 0) ? record.wins / (record.wins + record.losses) : 0;

  return (
    <Grid container spacing={2}>
      {/* Offensive Chart */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Offensive Profile</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={offenseData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.06)} />
                <XAxis type="number" domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="stat" width={50} tick={{ fontSize: 12, fontWeight: 600 }} />
                <RTooltip formatter={function(v) { return v.toFixed(3); }} />
                <Bar dataKey="value" fill={teamColor} radius={[0, 4, 4, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Pitching Chart */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Pitching Profile</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={pitchingData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.06)} />
                <XAxis type="number" domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="stat" width={50} tick={{ fontSize: 12, fontWeight: 600 }} />
                <RTooltip formatter={function(v) { return v.toFixed(2); }} />
                <Bar dataKey="value" fill={alpha(teamColor, 0.7)} radius={[0, 4, 4, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Stat Sections */}
      <Grid item xs={12} md={6}>
        <StatSection title="Team Record" color={theme.palette.primary.main} stats={[
          { label: 'Wins', value: record.wins, format: fmtInt, barPct: record.wins / 110 * 100 },
          { label: 'Losses', value: record.losses, format: fmtInt, barPct: record.losses / 110 * 100, invert: true },
          { label: 'Win %', value: winPctVal, format: fmtAvg, barPct: winPctVal * 100 },
          { label: 'Pyth W%', value: standings.pythagoreanWinPct || 0, format: fmtAvg, barPct: (standings.pythagoreanWinPct || 0) * 100 },
          { label: 'Run Diff', value: standings.runDifferential || 0, format: function(v) { return (v >= 0 ? '+' : '') + v; }, barPct: Math.min(100, Math.abs(standings.runDifferential || 0) / 200 * 100) },
        ]} />
      </Grid>

      <Grid item xs={12} md={6}>
        <StatSection title="Offensive Stats" color={theme.palette.warning.main} stats={[
          { label: 'Runs', value: batting.runs || 0, format: fmtInt, barPct: (batting.runs || 0) / 900 * 100 },
          { label: 'AVG', value: batting.avg || 0, format: fmtAvg, barPct: (batting.avg || 0) / 0.300 * 100 },
          { label: 'OBP', value: batting.obp || 0, format: fmtAvg, barPct: (batting.obp || 0) / 0.380 * 100 },
          { label: 'SLG', value: batting.slg || 0, format: fmtAvg, barPct: (batting.slg || 0) / 0.500 * 100 },
          { label: 'OPS', value: batting.ops || 0, format: fmtAvg, barPct: (batting.ops || 0) / 0.850 * 100 },
          { label: 'Home Runs', value: batting.homeRuns || 0, format: fmtInt, barPct: (batting.homeRuns || 0) / 280 * 100 },
          { label: 'wOBA', value: batting.wOBA || 0, format: fmtAvg, barPct: (batting.wOBA || 0) / 0.380 * 100 },
          { label: 'ISO', value: batting.iso || 0, format: fmtAvg, barPct: (batting.iso || 0) / 0.250 * 100 },
          { label: 'K%', value: batting.kRate || 0, format: fmtPct, barPct: (batting.kRate || 0) * 100, invert: true },
          { label: 'BB%', value: batting.bbRate || 0, format: fmtPct, barPct: (batting.bbRate || 0) / 0.12 * 100 },
        ]} />
      </Grid>

      <Grid item xs={12} md={6}>
        <StatSection title="Pitching Stats" color={theme.palette.error.main} stats={[
          { label: 'Runs Allowed', value: pitching.runs || 0, format: fmtInt, barPct: (pitching.runs || 0) / 900 * 100, invert: true },
          { label: 'ERA', value: pitching.era || 0, format: function(v) { return fmtDec(v); }, barPct: Math.max(0, (6 - (pitching.era || 4)) / 4 * 100), invert: true },
          { label: 'WHIP', value: pitching.whip || 0, format: function(v) { return fmtDec(v); }, barPct: Math.max(0, (1.8 - (pitching.whip || 1.3)) / 0.8 * 100), invert: true },
          { label: 'FIP', value: pitching.fip || 0, format: function(v) { return fmtDec(v); }, barPct: Math.max(0, (6 - (pitching.fip || 4)) / 4 * 100), invert: true },
          { label: 'K/9', value: pitching.strikeoutsPer9Inn || 0, format: function(v) { return fmtDec(v, 1); }, barPct: (pitching.strikeoutsPer9Inn || 0) / 12 * 100 },
          { label: 'BB/9', value: pitching.walksPer9Inn || 0, format: function(v) { return fmtDec(v, 1); }, barPct: Math.max(0, (5 - (pitching.walksPer9Inn || 3.5)) / 3 * 100), invert: true },
          { label: 'HR/9', value: pitching.homeRunsPer9 || 0, format: function(v) { return fmtDec(v, 1); }, barPct: Math.max(0, (2 - (pitching.homeRunsPer9 || 1.2)) / 1.5 * 100), invert: true },
          { label: 'BABIP', value: pitching.babip || 0, format: fmtAvg, barPct: (pitching.babip || 0) / 0.350 * 100 },
          { label: 'LOB%', value: pitching.leftOnBasePercentage || 0, format: function(v) { return fmtDec(v, 1) + '%'; }, barPct: pitching.leftOnBasePercentage || 0 },
          { label: 'IP', value: pitching.inningsPitched || 0, format: function(v) { return String(v); }, barPct: parseFloat(pitching.inningsPitched || 0) / 1500 * 100 },
        ]} />
      </Grid>

      <Grid item xs={12} md={6}>
        <StatSection title="Fielding Stats" color={theme.palette.info.main} stats={[
          { label: 'Fielding %', value: fielding.fieldingPercentage || 0, format: fmtAvg, barPct: (fielding.fieldingPercentage || 0.98) * 100 },
          { label: 'Errors', value: fielding.errors || 0, format: fmtInt, barPct: (fielding.errors || 0) / 150 * 100, invert: true },
          { label: 'Assists', value: fielding.assists || 0, format: fmtInt, barPct: (fielding.assists || 0) / 1800 * 100 },
          { label: 'Putouts', value: fielding.putOuts || 0, format: fmtInt, barPct: (fielding.putOuts || 0) / 4400 * 100 },
          { label: 'Chances', value: fielding.chances || 0, format: fmtInt, barPct: (fielding.chances || 0) / 6200 * 100 },
        ]} />
      </Grid>
    </Grid>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// GAME LOG TAB - clickable rows linking to boxscores
// ════════════════════════════════════════════════════════════════════════════
const GameLogTab = ({ gameLog, teamId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const teamColor = themeUtils.getTeamColor(teamId) || '#1976d2';

  const summary = useMemo(function() {
    if (!gameLog || !gameLog.length) return null;
    const w = gameLog.filter(function(g) { return g.result === 'W'; }).length;
    const l = gameLog.filter(function(g) { return g.result === 'L'; }).length;
    return { w: w, l: l, gp: gameLog.length, pct: (w + l > 0 ? (w / (w + l)).toFixed(3) : '\u2014') };
  }, [gameLog]);

  const Sparkline = function() {
    var recent = (gameLog || []).slice(-50);
    if (recent.length < 3) return null;
    var W = Math.min(400, recent.length * 8);
    var H = 32;
    var bw = W / recent.length;
    return (
      <svg width="100%" height={H} viewBox={'0 0 ' + W + ' ' + H} preserveAspectRatio="none" style={{ display: 'block' }}>
        {recent.map(function(g, i) {
          return (
            <rect key={i} x={i * bw + 0.5} y={g.result === 'W' ? 2 : H / 2}
              width={Math.max(bw - 1, 2)} height={H / 2 - 2} rx={1}
              fill={g.result === 'W' ? alpha('#4caf50', 0.7) : alpha('#f44336', 0.5)} />
          );
        })}
      </svg>
    );
  };

  var fmtDate = function(game) {
    try {
      var src = game.date || (game.gameInfo && game.gameInfo.officialDate);
      if (!src) return '\u2014';
      return new Date(src + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) { return game.date || '\u2014'; }
  };

  if (!gameLog || !gameLog.length) return (
    <Card elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography color="text.secondary">No games available</Typography>
    </Card>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Sparkline */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 2, pb: '12px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Win/Loss Streak</Typography>
            <Typography variant="caption" color="text.secondary">Last {Math.min(50, gameLog.length)} games</Typography>
          </Box>
          <Sparkline />
        </CardContent>
      </Card>

      {summary && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label={summary.gp + ' GP'} sx={{ fontWeight: 700, bgcolor: alpha(teamColor, 0.1) }} />
          <Chip size="small" label={summary.w + 'W'} sx={{ fontWeight: 700, color: '#4caf50' }} />
          <Chip size="small" label={summary.l + 'L'} sx={{ fontWeight: 700, color: '#f44336' }} />
          <Chip size="small" label={summary.pct + ' W%'} sx={{ fontWeight: 600 }} />
        </Box>
      )}

      {/* Games list - CLICKABLE rows to Boxscore */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Typography variant="h6" fontWeight={700} sx={{ px: 2.5, pt: 2, pb: 1, fontSize: '0.95rem' }}>
            Game Log ({gameLog.length})
          </Typography>
          {gameLog.slice().reverse().map(function(game, idx) {
            var isWin = game.result === 'W';
            var score = game.score || { team: 0, opponent: 0 };
            var borderColor = isWin ? '#4caf50' : (game.result === 'L' ? '#f44336' : theme.palette.divider);

            return (
              <Box key={idx}
                onClick={function() {
                  if (game.gameId && game.date) navigate('/boxscore/' + game.gameId + '/' + game.date);
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.25, sm: 1 },
                  minHeight: { xs: 48, sm: 'auto' },
                  borderLeft: '3px solid ' + borderColor,
                  borderBottom: '1px solid ' + alpha(theme.palette.divider, 0.5),
                  cursor: game.gameId ? 'pointer' : 'default',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: alpha(teamColor, 0.06) },
                }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', minWidth: 60, flexShrink: 0 }}>
                  {fmtDate(game)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 80, flex: 1 }}>
                  <Avatar src={getTeamLogoUrl(game.opponent)} alt={game.opponent}
                    sx={{ width: 20, height: 20 }}
                    imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>
                    {(game.opponent || '?')[0]}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                    {game.homeAway === 'home' ? 'vs' : '@'} {game.opponent || '\u2014'}
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 26, height: 26, borderRadius: '50%',
                  bgcolor: isWin ? alpha('#4caf50', 0.15) : alpha('#f44336', 0.15),
                  color: isWin ? '#4caf50' : '#f44336',
                  fontWeight: 800, fontSize: '0.75rem', flexShrink: 0,
                }}>{game.result || '—'}</Box>

                <Typography variant="body2" fontWeight={700} sx={{
                  fontSize: '0.85rem', minWidth: 44, textAlign: 'center',
                  color: isWin ? 'success.main' : 'error.main',
                }}>{score.team}-{score.opponent}</Typography>

                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: 'right', fontSize: '0.75rem' }}>
                  {game.teamRecord || ''}
                </Typography>
              </Box>
            );
          })}
        </CardContent>
      </Card>
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// ROSTER TAB - sortable table matching /players endpoint
// ════════════════════════════════════════════════════════════════════════════
const RosterTab = ({ roster, teamId, year, teamColor }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [view, setView] = useState('batting');
  const [sortBy, setSortBy] = useState('war');
  const [sortOrder, setSortOrder] = useState('desc');

  const batters = useMemo(function() {
    return roster.filter(function(p) { return (p.stats && p.stats.batting && p.stats.batting.atBats > 0) || (p.stats && p.stats.batting && p.stats.batting.plateAppearances > 0); });
  }, [roster]);
  const pitchers = useMemo(function() {
    return roster.filter(function(p) { return parseFloat((p.stats && p.stats.pitching && p.stats.pitching.inningsPitched) || 0) > 0 || (p.stats && p.stats.pitching && p.stats.pitching.gamesPlayed > 0); });
  }, [roster]);
  const players = view === 'batting' ? batters : pitchers;

  const sorted = useMemo(function() {
    return players.slice().sort(function(a, b) {
      var aV = getNestedValue(a, sortBy);
      var bV = getNestedValue(b, sortBy);
      if (aV == null && bV == null) return 0;
      if (aV == null) return 1;
      if (bV == null) return -1;
      var aN = typeof aV === 'string' ? parseFloat(aV) : aV;
      var bN = typeof bV === 'string' ? parseFloat(bV) : bV;
      var c = (typeof aN === 'number' && typeof bN === 'number' && !isNaN(aN) && !isNaN(bN)) ? aN - bN : String(aV).localeCompare(String(bV));
      return sortOrder === 'desc' ? -c : c;
    });
  }, [players, sortBy, sortOrder]);

  var handleSort = function(key) {
    if (key === sortBy) setSortOrder(function(o) { return o === 'desc' ? 'asc' : 'desc'; });
    else { setSortBy(key); setSortOrder('desc'); }
  };

  var battingCols = [
    { key: 'war', label: 'WAR', fmt: function(v) { return fmtDec(v, 2); } },
    { key: 'cvr', label: 'CVR', fmt: fmtInt },
    { key: 'salary.salary', label: 'Salary', fmt: fmtSalary },
    { key: 'stats.batting.avg', label: 'AVG', fmt: fmtAvg },
    { key: 'stats.batting.obp', label: 'OBP', fmt: fmtAvg },
    { key: 'stats.batting.slg', label: 'SLG', fmt: fmtAvg },
    { key: 'stats.batting.ops', label: 'OPS', fmt: fmtAvg },
    { key: 'stats.batting.homeRuns', label: 'HR', fmt: fmtInt },
    { key: 'stats.batting.rbi', label: 'RBI', fmt: fmtInt },
    { key: 'stats.batting.runs', label: 'R', fmt: fmtInt },
    { key: 'stats.batting.hits', label: 'H', fmt: fmtInt },
    { key: 'stats.batting.strikeOuts', label: 'K', fmt: fmtInt },
    { key: 'stats.batting.baseOnBalls', label: 'BB', fmt: fmtInt },
  ];
  var pitchingCols = [
    { key: 'war', label: 'WAR', fmt: function(v) { return fmtDec(v, 2); } },
    { key: 'cvr', label: 'CVR', fmt: fmtInt },
    { key: 'salary.salary', label: 'Salary', fmt: fmtSalary },
    { key: 'stats.pitching.era', label: 'ERA', fmt: function(v) { return fmtDec(v); } },
    { key: 'stats.pitching.whip', label: 'WHIP', fmt: function(v) { return fmtDec(v); } },
    { key: 'stats.pitching.inningsPitched', label: 'IP', fmt: function(v) { return String(v || '\u2014'); } },
    { key: 'stats.pitching.strikeOuts', label: 'K', fmt: fmtInt },
    { key: 'stats.pitching.baseOnBalls', label: 'BB', fmt: fmtInt },
    { key: 'stats.pitching.wins', label: 'W', fmt: fmtInt },
    { key: 'stats.pitching.losses', label: 'L', fmt: fmtInt },
    { key: 'stats.pitching.saves', label: 'SV', fmt: fmtInt },
  ];
  var allCols = view === 'batting' ? battingCols : pitchingCols;
  var mobileKeys = view === 'batting'
    ? ['war', 'stats.batting.avg', 'stats.batting.ops', 'stats.batting.homeRuns']
    : ['war', 'stats.pitching.era', 'stats.pitching.whip', 'stats.pitching.strikeOuts'];
  var cols = isMobile ? allCols.filter(function(c) { return mobileKeys.indexOf(c.key) >= 0; }) : allCols;

  // Savant-style percentile tints per column (same bands as Players.js)
  const heatMaps = useMemo(function() {
    var colKeys = (view === 'batting' ? battingCols : pitchingCols).map(function(c) { return c.key; });
    var m = {};
    colKeys.forEach(function(k) {
      m[k] = buildHeatMap(players, k, INVERT_STATS.has(k));
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, view]);

  var SortHeader = function({ col }) {
    var active = sortBy === col.key;
    return (
      <TableCell align="center" sx={{ cursor: 'pointer', userSelect: 'none', py: 0.5,
        '&:hover': { bgcolor: alpha(teamColor, 0.05) },
        ...(active && { bgcolor: alpha(teamColor, 0.1), color: teamColor }) }}
        onClick={function() { handleSort(col.key); }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{col.label}</Typography>
          {active && (sortOrder === 'desc' ? <ArrowDownward sx={{ fontSize: '0.75rem' }} /> : <ArrowUpward sx={{ fontSize: '0.75rem' }} />)}
        </Box>
      </TableCell>
    );
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 0 }}>
        {/* Toggle */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
            Roster ({sorted.length} {view === 'batting' ? 'Batters' : 'Pitchers'})
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Chip label={'Batters (' + batters.length + ')'} variant={view === 'batting' ? 'filled' : 'outlined'}
              color={view === 'batting' ? 'primary' : 'default'} size="small"
              onClick={function() { setView('batting'); setSortBy('war'); }} sx={{ cursor: 'pointer', fontWeight: 600 }} />
            <Chip label={'Pitchers (' + pitchers.length + ')'} variant={view === 'pitching' ? 'filled' : 'outlined'}
              color={view === 'pitching' ? 'primary' : 'default'} size="small"
              onClick={function() { setView('pitching'); setSortBy('war'); }} sx={{ cursor: 'pointer', fontWeight: 600 }} />
          </Box>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: { xs: 400, sm: 600 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>Player</Typography>
                </TableCell>
                {!isMobile && (
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>POS</Typography>
                </TableCell>
                )}
                {!isMobile && (
                <TableCell align="center" sx={{ py: 0.5, cursor: 'pointer' }} onClick={function() { handleSort('gameCount'); }}>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>G</Typography>
                </TableCell>
                )}
                {cols.map(function(c) { return <SortHeader key={c.key + c.label} col={c} />; })}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(function(p, i) {
                return (
                  <TableRow key={i} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(teamColor, 0.04) } }}
                    onClick={function() { navigate('/players/profile/' + makeSlug(p.name)); }}>
                    <TableCell sx={{ py: 0.6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!isMobile && (
                        <Avatar src={getTeamLogoUrl(teamId)} sx={{ width: 28, height: 28, bgcolor: teamColor, fontSize: '0.75rem', fontWeight: 700 }}
                          imgProps={{ style: { objectFit: 'contain', background: 'white' } }}>{p.name && p.name[0]}</Avatar>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>{p.name}</Typography>
                          {p.salary && p.salary.salary > 0 && !isMobile && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{fmtSalary(p.salary.salary)}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {!isMobile && (
                    <TableCell align="center">
                      <Chip label={p.position || '\u2014'} size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: '0.75rem' }} />
                    </TableCell>
                    )}
                    {!isMobile && (
                    <TableCell align="center"><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{p.gameCount || 0}</Typography></TableCell>
                    )}
                    {cols.map(function(c) {
                      var val = getNestedValue(p, c.key);
                      var hm = (heatMaps[c.key] && heatMaps[c.key][p.name]) || null;
                      var tinted = hm && hm.fg !== 'inherit';
                      return (
                        <TableCell key={c.key + c.label} align="center" sx={{ bgcolor: hm ? hm.bg : 'transparent' }}>
                          <Typography variant="body2" fontWeight={tinted ? 700 : 500}
                            sx={{ fontSize: '0.8rem', color: tinted ? hm.fg : 'text.secondary' }}>
                            {c.fmt(val)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {sorted.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No {view === 'batting' ? 'batters' : 'pitchers'} with sufficient playing time.</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CONTRACTS TAB
// ════════════════════════════════════════════════════════════════════════════
// Analytics Tab — WAR Distribution, Team Needs, Splits
// ════════════════════════════════════════════════════════════════════════════
const AnalyticsTab = ({ teamId, year, teamColor, roster = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [analysis, setAnalysis] = useState(null);
  const [splits, setSplits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Rotation vs Bullpen — aggregated from the roster (weighted by raw counts)
  const rotationBullpen = useMemo(() => {
    const parseIP = (ip) => {
      if (ip == null) return 0;
      const s = String(ip);
      const [whole, frac] = s.split('.');
      return (parseInt(whole, 10) || 0) + ((parseInt(frac, 10) || 0) / 3);
    };
    const pitchers = roster.filter(p => ['Pitcher', 'SP', 'RP', 'CP'].includes(p.playerType));
    if (!pitchers.length) return null;
    const emptyAgg = () => ({ arms: 0, ip: 0, er: 0, h: 0, bb: 0, k: 0, hr: 0, war: 0, sv: 0 });
    const rot = emptyAgg(); const pen = emptyAgg();
    for (const p of pitchers) {
      const pit = (p.stats && p.stats.pitching) || {};
      const ip = parseIP(pit.inningsPitched);
      if (ip <= 0) continue;
      const gs = pit.gamesStarted || 0;
      const g = pit.gamesPlayed || 0;
      // SP if labelled SP, or majority of appearances are starts
      const isStarter = p.playerType === 'SP' || (gs > 0 && g > 0 && gs / g >= 0.5);
      const agg = isStarter ? rot : pen;
      agg.arms += 1;
      agg.ip += ip;
      agg.er += pit.earnedRuns || 0;
      agg.h += pit.hits || 0;
      agg.bb += pit.baseOnBalls || 0;
      agg.k += pit.strikeOuts || 0;
      agg.hr += pit.homeRuns || 0;
      agg.war += p.pwar != null ? p.pwar : (p.war || 0);
      agg.sv += pit.saves || 0;
    }
    if (rot.ip === 0 && pen.ip === 0) return null;
    const rates = (a) => ({
      ...a,
      era: a.ip > 0 ? (a.er * 9) / a.ip : null,
      whip: a.ip > 0 ? (a.h + a.bb) / a.ip : null,
      k9: a.ip > 0 ? (a.k * 9) / a.ip : null,
      bb9: a.ip > 0 ? (a.bb * 9) / a.ip : null,
      hr9: a.ip > 0 ? (a.hr * 9) / a.ip : null,
    });
    return { rotation: rates(rot), bullpen: rates(pen) };
  }, [roster]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      analyticsApi.getTeamAnalysis(teamId, year).catch(() => null),
      teamsApi.getTeamSplits(teamId, { year }).catch(() => null),
    ]).then(([a, s]) => {
      if (!cancelled) { setAnalysis(a); setSplits(s); }
    }).catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, year]);

  if (loading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Typography color="error">{error}</Typography></Box>;

  const rosterPlayers = analysis?.roster?.players || [];
  const parkFactors = analysis?.parkFactors || {};
  const splitData = splits?.splits || {};
  const insights = splits?.insights || [];

  /* Home/Away comparison */
  const homeAway = splitData.homeAway;
  const homeAwayRows = homeAway ? [
    { label: 'Record', home: `${homeAway.home?.wins || 0}-${homeAway.home?.losses || 0}`, away: `${homeAway.away?.wins || 0}-${homeAway.away?.losses || 0}` },
    { label: 'Win %', home: ((homeAway.home?.winPercentage || 0) * 100).toFixed(1) + '%', away: ((homeAway.away?.winPercentage || 0) * 100).toFixed(1) + '%' },
    { label: 'Runs', home: homeAway.home?.batting?.runs || 0, away: homeAway.away?.batting?.runs || 0 },
    { label: 'Hits', home: homeAway.home?.batting?.hits || 0, away: homeAway.away?.batting?.hits || 0 },
    { label: 'HR', home: homeAway.home?.batting?.homeRuns || 0, away: homeAway.away?.batting?.homeRuns || 0 },
    { label: 'ERA', home: homeAway.home?.pitching?.era || '—', away: homeAway.away?.pitching?.era || '—' },
  ] : [];

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Park Factors */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.85rem' }}>
                Park Factors
              </Typography>
              <Grid container spacing={1}>
                {[
                  { label: 'Runs', val: parkFactors.runs },
                  { label: 'HR', val: parkFactors.hr || parkFactors.homeRuns },
                  { label: 'Hits', val: parkFactors.hits || parkFactors.h },
                  { label: 'Doubles', val: parkFactors.doubles || parkFactors['2b'] },
                ].filter(pf => pf.val != null).map((pf, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{ textAlign: 'center', p: 0.75, borderRadius: 1, bgcolor: alpha(theme.palette.divider, 0.06) }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>{pf.label}</Typography>
                      <Typography variant="body2" fontWeight={700} fontFamily="monospace" sx={{
                        color: pf.val > 105 ? '#e74c3c' : pf.val < 95 ? '#2e7d32' : 'text.primary',
                      }}>
                        {typeof pf.val === 'number' ? pf.val.toFixed(0) : pf.val}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {Object.keys(parkFactors).length === 0 && (
                <Typography variant="caption" color="text.secondary">Park factor data unavailable</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Home/Away Splits */}
        {homeAwayRows.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.85rem' }}>
                  Home vs Away Splits
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Stat</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>🏠 Home</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>✈️ Away</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {homeAwayRows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{row.label}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.home}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.away}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Monthly Performance - removed per user feedback */}

        {/* Rotation vs Bullpen */}
        {rotationBullpen && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.85rem' }}>
                  Rotation vs Bullpen
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Unit</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Arms</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>IP</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>ERA</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>WHIP</TableCell>
                        {!isMobile && <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>K/9</TableCell>}
                        {!isMobile && <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>BB/9</TableCell>}
                        {!isMobile && <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>HR/9</TableCell>}
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>WAR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        { label: 'Rotation', data: rotationBullpen.rotation, color: teamColor },
                        { label: 'Bullpen', data: rotationBullpen.bullpen, color: '#5c6bc0' },
                      ].map(({ label, data, color }) => (
                        <TableRow key={label}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color }}>{label}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{data.arms}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{data.ip.toFixed(1)}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700,
                            color: data.era != null && data.era < 4 ? '#2e7d32' : data.era != null && data.era > 4.8 ? '#c62828' : 'text.primary' }}>
                            {data.era != null ? data.era.toFixed(2) : '\u2014'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{data.whip != null ? data.whip.toFixed(2) : '\u2014'}</TableCell>
                          {!isMobile && <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{data.k9 != null ? data.k9.toFixed(1) : '\u2014'}</TableCell>}
                          {!isMobile && <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{data.bb9 != null ? data.bb9.toFixed(1) : '\u2014'}</TableCell>}
                          {!isMobile && <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{data.hr9 != null ? data.hr9.toFixed(1) : '\u2014'}</TableCell>}
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700 }}>{data.war.toFixed(1)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Insights */}
        {insights.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75, fontSize: '0.85rem' }}>
                  Insights
                </Typography>
                {insights.map((insight, i) => (
                  <Chip key={i} label={insight.message} size="small" variant="outlined"
                    sx={{ mr: 0.75, mb: 0.5, fontSize: '0.72rem', fontWeight: 600 }} />
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Full Roster WAR Table */}
        {rosterPlayers.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                    Roster WAR Breakdown — {rosterPlayers.length} Players
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Player</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Pos</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>G</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>WAR</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>CVR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rosterPlayers.map((p, i) => (
                        <TableRow key={p.playerId || i} hover sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/players/profile/${makeSlug(p.name)}`)}>
                          <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{i + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem' }}>{p.name}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={p.position} size="small" sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600,
                              bgcolor: p.isPitcher ? alpha('#5c6bc0', 0.1) : alpha(teamColor, 0.1) }} />
                          </TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.games}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700,
                            color: p.war >= 3 ? '#2e7d32' : p.war >= 1 ? teamColor : p.war < 0 ? '#c62828' : 'text.primary'
                          }}>{(p.war || 0).toFixed(1)}</TableCell>
                          <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                            {p.cvr ? parseFloat(p.cvr).toFixed(1) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Contracts Tab
// ════════════════════════════════════════════════════════════════════════════
const ContractsTab = ({ teamId, year, teamColor }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('salary');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    teamsApi.getTeamContracts(teamId, { year, sortBy })
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, year, sortBy]);

  if (loading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Typography color="error">{error}</Typography></Box>;
  if (!data || !data.contracts) return <Box sx={{ p: 3 }}><Typography color="text.secondary">No contract data available.</Typography></Box>;

  const c = data.contracts;
  const roster = c.roster || [];

  const contractTypeColors = {
    'pre-arb': '#2196F3', 'arb-eligible': '#FF9800', 'arbitration': '#FF9800',
    'free-agent': '#4CAF50', 'extension': '#9C27B0', 'unknown': '#757575',
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 0 }}>
        {/* Summary */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Team Contracts — {c.playerCount || roster.length} Players
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Payroll: {fmtSalary(c.totalPayroll || 0)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {['salary', 'war', 'name'].map(s => (
              <Chip key={s} label={s === 'salary' ? 'Salary' : s === 'war' ? 'WAR' : 'Name'}
                size="small"
                variant={sortBy === s ? 'filled' : 'outlined'}
                color={sortBy === s ? 'primary' : 'default'}
                onClick={() => setSortBy(s)}
                sx={{ cursor: 'pointer', fontWeight: 600 }} />
            ))}
          </Box>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: { xs: 320, sm: 600 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>Player</Typography></TableCell>
                {!isMobile && <TableCell align="center" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>POS</Typography></TableCell>}
                <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>Salary</Typography></TableCell>
                {!isMobile && <TableCell align="center" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>Type</Typography></TableCell>}
                <TableCell align="center" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>WAR</Typography></TableCell>
                {!isMobile && <TableCell align="center" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>CVR</Typography></TableCell>}
                {!isMobile && <TableCell align="center" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>Svc Time</Typography></TableCell>}
                {!isMobile && <TableCell align="center" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>Ctrl Yrs</Typography></TableCell>}
                {!isMobile && <TableCell align="right" sx={{ py: 0.5 }}><Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>% Payroll</Typography></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {roster.map((p, i) => {
                const typeColor = contractTypeColors[p.contractType] || '#757575';
                const typeLabel = (p.contractType || 'unknown').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                const payPct = c.totalPayroll > 0 && p.salary > 0 ? ((p.salary / c.totalPayroll) * 100).toFixed(1) : '—';
                return (
                  <TableRow key={i} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(teamColor, 0.04) } }}
                    onClick={() => navigate('/players/profile/' + makeSlug(p.name))}>
                    <TableCell sx={{ py: 0.6 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{p.name}</Typography>
                    </TableCell>
                    {!isMobile && (
                    <TableCell align="center">
                      <Chip label={p.position || 'u2014'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                    </TableCell>
                    )}
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {fmtSalary(p.salary)}
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                    <TableCell align="center">
                      <Chip label={typeLabel} size="small" sx={{
                        height: 20, fontSize: '0.75rem', fontWeight: 600,
                        bgcolor: alpha(typeColor, 0.12), color: typeColor, border: 'none',
                      }} />
                    </TableCell>
                    )}
                    <TableCell align="center">
                      {(() => {
                        // Show whichever component (rWAR vs pWAR) is more
                        // meaningful. Two-way players have both > 0.
                        const w = Math.max(p.war || 0, p.pwar || 0);
                        return (
                          <Typography variant="body2" fontWeight={w >= 2 ? 700 : 400}
                            sx={{ fontSize: '0.8rem', color: w >= 3 ? '#4CAF50' : w >= 1 ? 'text.primary' : 'text.secondary' }}>
                            {w.toFixed(2)}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    {!isMobile && (
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {p.universalCVR ? Math.round(p.universalCVR) : '—'}
                      </Typography>
                    </TableCell>
                    )}
                    {!isMobile && (
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {(p.serviceTime && typeof p.serviceTime === 'object') ? (p.serviceTime.total || `${p.serviceTime.years || 0}.${String(p.serviceTime.days || 0).padStart(3, '0')}`) : (p.serviceTime || '—')}
                      </Typography>
                    </TableCell>
                    )}
                    {!isMobile && (
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {p.controlYears != null ? p.controlYears : '—'}
                      </Typography>
                    </TableCell>
                    )}
                    {!isMobile && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {payPct !== '—' ? payPct + '%' : '—'}
                        </Typography>
                        {payPct !== '—' && (
                          <LinearProgress variant="determinate" value={Math.min(parseFloat(payPct), 100)}
                            sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: alpha(teamColor, 0.1),
                              '& .MuiLinearProgress-bar': { bgcolor: teamColor, borderRadius: 2 } }} />
                        )}
                      </Box>
                    </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {roster.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No contract data found for this team.</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Skeleton Loader
// ════════════════════════════════════════════════════════════════════════════
const TeamDetailSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
    <Skeleton variant="text" width={80} height={32} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mb: 2 }} />
    <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 2, mb: 2 }} />
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map(function(i) {
        return <Grid item xs={6} sm={3} key={i}><Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} /></Grid>;
      })}
      <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} /></Grid>
      <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} /></Grid>
    </Grid>
  </Box>
);

export default TeamDetail;
